import React, { useState, useEffect, useRef } from 'react';
import { ref, onValue, update, set, push, runTransaction } from 'firebase/database';
import { createUserWithEmailAndPassword, signOut } from 'firebase/auth';
import { QRCodeSVG } from 'qrcode.react';
import { db, auth, secondaryAuth } from './firebase';
import { logAdminAction } from './auditLog';

const BACKEND_URL = 'https://pharmdev-rider.onrender.com';

const inp = {
  padding: '9px 12px', borderRadius: 8, border: '1px solid #E5E7EB',
  fontSize: 13, backgroundColor: '#F7F8FA', outline: 'none',
};

// Shared between worker creation (staff ID prefix) and the Send Update
// department picker.
const DEPARTMENTS = [
  { code: 'CS', label: 'Customer Service' },
  { code: 'FI', label: 'Finance' },
  { code: 'OP', label: 'Operations' },
  { code: 'MK', label: 'Marketing' },
  { code: 'HR', label: 'Human Resources' },
  { code: 'IT', label: 'IT' },
  { code: 'MG', label: 'Management' },
  { code: 'OT', label: 'Other' },
];
const departmentLabel = (code) => DEPARTMENTS.find(d => d.code === code)?.label || code;

const DEFAULT_SCREENING_QUESTIONS = [
  'Why are you interested in this role at PharmDev?',
  'Tell us about a time you handled a difficult customer or a stressful situation.',
  'What relevant experience do you have for this position?',
];

// Atomically claims the next number for a department prefix (workerIdCounters/{prefix})
// so two admins creating a worker in the same department at the same moment can
// never collide — e.g. CS1001, CS1002, ... starting at 1001.
async function generateStaffId(departmentCode) {
  const counterRef = ref(db, `workerIdCounters/${departmentCode}`);
  const result = await runTransaction(counterRef, (current) => (current || 1000) + 1);
  return `${departmentCode}${result.snapshot.val()}`;
}

function StatusBadge(worker) {
  if (worker.suspended) return { bg: '#FEE2E2', color: '#B91C1C', label: '🚫 Suspended' };
  if (worker.isApproved) return { bg: '#DCFCE7', color: '#15803D', label: '✅ Active' };
  return { bg: '#FEF3C7', color: '#92400E', label: '⏳ Pending' };
}

async function authedFetch(path, body, method = 'POST') {
  const token = await auth.currentUser.getIdToken();
  const res = await fetch(`${BACKEND_URL}${path}`, {
    method,
    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
    body: body ? JSON.stringify(body) : undefined,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `Request to ${path} failed`);
  return data;
}

export default function HRPayrollTab() {
  const [activeView, setActiveView] = useState('workers');
  const [workers, setWorkers] = useState([]);
  const [attendance, setAttendance] = useState({});
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [search, setSearch] = useState('');
  const [updating, setUpdating] = useState(null);
  const [rateEdits, setRateEdits] = useState({});
  const [balanceModal, setBalanceModal] = useState(null);

  useEffect(() => {
    const unsub1 = onValue(ref(db, 'workers'), snap => {
      setWorkers(snap.exists() ? Object.entries(snap.val()).map(([id, v]) => ({ id, ...v })) : []);
    });
    const unsub2 = onValue(ref(db, 'attendance'), snap => {
      setAttendance(snap.exists() ? snap.val() : {});
    });
    const unsub3 = onValue(ref(db, 'leaveRequests'), snap => {
      setLeaveRequests(snap.exists() ? Object.entries(snap.val()).map(([id, v]) => ({ id, ...v })) : []);
    });
    return () => { unsub1(); unsub2(); unsub3(); };
  }, []);

  const saveLeaveBalances = async (worker, balances) => {
    setUpdating(worker.id);
    try {
      await update(ref(db, `workers/${worker.id}/leaveBalances`), balances);
      await logAdminAction('set_worker_leave_balances', worker.name, { workerId: worker.id, balances });
      setBalanceModal(null);
    } catch { alert('Error saving leave balances'); }
    finally { setUpdating(null); }
  };

  const reviewLeaveRequest = async (request, status) => {
    let reviewText = '';
    if (status === 'rejected') {
      const reason = window.prompt(`Reason for rejecting ${request.workerName}'s leave request (optional):`);
      if (reason === null) return; // cancelled
      reviewText = reason.trim();
    }
    setUpdating(request.id);
    try {
      const payload = { status, reviewedAt: new Date().toISOString(), reviewedBy: auth.currentUser?.email || '', reviewText };
      await update(ref(db), {
        [`leaveRequests/${request.id}/status`]: payload.status,
        [`leaveRequests/${request.id}/reviewedAt`]: payload.reviewedAt,
        [`leaveRequests/${request.id}/reviewedBy`]: payload.reviewedBy,
        [`leaveRequests/${request.id}/reviewText`]: payload.reviewText,
        [`workers/${request.workerId}/leaveRequests/${request.id}/status`]: payload.status,
        [`workers/${request.workerId}/leaveRequests/${request.id}/reviewedAt`]: payload.reviewedAt,
        [`workers/${request.workerId}/leaveRequests/${request.id}/reviewedBy`]: payload.reviewedBy,
        [`workers/${request.workerId}/leaveRequests/${request.id}/reviewText`]: payload.reviewText,
      });
      await logAdminAction(status === 'approved' ? 'approve_leave_request' : 'reject_leave_request', request.workerName, { requestId: request.id, reviewText });
    } catch { alert('Error updating leave request'); }
    finally { setUpdating(null); }
  };

  const filteredWorkers = workers.filter(w =>
    !search || w.name?.toLowerCase().includes(search.toLowerCase()) ||
    w.email?.toLowerCase().includes(search.toLowerCase()) ||
    w.phone?.toLowerCase().includes(search.toLowerCase())
  );

  const approveWorker = async (w) => {
    setUpdating(w.id);
    try {
      await update(ref(db, `workers/${w.id}`), { isApproved: true, approvedAt: new Date().toISOString() });
      await logAdminAction('approve_worker', w.name, { workerId: w.id });
    } catch { alert('Error approving worker'); }
    finally { setUpdating(null); }
  };

  const toggleSuspend = async (w) => {
    const next = !w.suspended;
    setUpdating(w.id);
    try {
      await update(ref(db, `workers/${w.id}`), { suspended: next, suspendedAt: next ? new Date().toISOString() : null });
      await logAdminAction(next ? 'suspend_worker' : 'reactivate_worker', w.name, { workerId: w.id });
    } catch { alert('Error updating worker status'); }
    finally { setUpdating(null); }
  };

  const saveRate = async (w) => {
    const rate = parseFloat(rateEdits[w.id]);
    if (isNaN(rate) || rate < 0) return alert('Enter a valid hourly rate');
    setUpdating(w.id);
    try {
      await update(ref(db, `workers/${w.id}`), { payRateGHS: rate });
      await logAdminAction('set_worker_pay_rate', w.name, { workerId: w.id, payRateGHS: rate });
      setRateEdits(prev => { const next = { ...prev }; delete next[w.id]; return next; });
    } catch { alert('Error saving pay rate'); }
    finally { setUpdating(null); }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div>
          <h2 style={{ fontWeight: 700, margin: 0, fontSize: 18 }}>🧾 HR & Payroll</h2>
          <p style={{ color: '#888', fontSize: 13, margin: '4px 0 0' }}>Superadmin only — office/support staff, separate from delivery riders</p>
        </div>
        <span style={{ backgroundColor: '#FEE2E2', color: '#B91C1C', padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600 }}>
          🔒 Superadmin Only
        </span>
      </div>

      <div style={{ display: 'flex', gap: 4, marginBottom: 20, backgroundColor: '#F1F3F5', borderRadius: 10, padding: 4, width: 'fit-content', flexWrap: 'wrap' }}>
        {[
          { id: 'workers',       label: `👤 Workers (${workers.length})` },
          { id: 'clock_station', label: '📷 Clock-In Station' },
          { id: 'attendance',    label: '🕒 Attendance Log' },
          { id: 'leave',         label: `🌴 Leave Requests${leaveRequests.filter(r=>r.status==='pending').length ? ` (${leaveRequests.filter(r=>r.status==='pending').length})` : ''}` },
          { id: 'send_update',   label: '📢 Send Update' },
          { id: 'recruitment',   label: '🧑‍💻 Recruitment' },
          { id: 'payroll',       label: '💵 Run Payroll' },
        ].map(t => (
          <button key={t.id} style={{ padding: '8px 16px', borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: activeView === t.id ? 700 : 400, backgroundColor: activeView === t.id ? '#FFF' : 'transparent', color: activeView === t.id ? '#1D9E75' : '#888' }}
            onClick={() => setActiveView(t.id)}>
            {t.label}
          </button>
        ))}
      </div>

      {activeView === 'workers' && (
        <WorkersView
          workers={filteredWorkers}
          search={search} setSearch={setSearch}
          updating={updating}
          approveWorker={approveWorker}
          toggleSuspend={toggleSuspend}
          rateEdits={rateEdits} setRateEdits={setRateEdits}
          saveRate={saveRate}
          onEditBalances={setBalanceModal}
        />
      )}

      {activeView === 'clock_station' && <ClockStationView />}

      {activeView === 'attendance' && <AttendanceView workers={workers} attendance={attendance} />}

      {activeView === 'leave' && (
        <LeaveRequestsView requests={leaveRequests} updating={updating} onReview={reviewLeaveRequest} />
      )}

      {activeView === 'send_update' && <SendUpdateView workers={workers} />}

      {activeView === 'recruitment' && <RecruitmentView />}

      {activeView === 'payroll' && <PayrollView workers={workers} />}

      {balanceModal && (
        <LeaveBalanceModal
          worker={balanceModal}
          saving={updating === balanceModal.id}
          onSave={(balances) => saveLeaveBalances(balanceModal, balances)}
          onClose={() => setBalanceModal(null)}
        />
      )}
    </div>
  );
}

// ── CREATE WORKER ACCOUNT ────────────────────────────────────────────────
// Workers don't self-register — an admin creates their login here and
// hands the email/password to them directly, mirroring how admin accounts
// are created in the Users tab (App.js createAdminUser). Uses secondaryAuth
// so creating the new account doesn't sign the acting admin out.
// Exported (not just used internally) so the Users tab can offer the same
// worker-account creation module without duplicating this logic — see
// WorkerAccountsModule in App.js.
export function CreateWorkerForm({ source }) {
  const [form, setForm] = useState({ name: '', phone: '', email: '', password: '', payRateGHS: '', department: 'CS' });
  const [creating, setCreating] = useState(false);
  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const createWorker = async () => {
    if (!form.name.trim() || !form.phone.trim() || !form.email.trim() || !form.password) {
      alert('Fill in name, phone, email, and password'); return;
    }
    if (form.password.length < 6) { alert('Password must be at least 6 characters'); return; }
    setCreating(true);
    try {
      // Claim the staff ID before creating the account — if this fails,
      // nothing else has happened yet.
      const staffId = await generateStaffId(form.department);
      const cred = await createUserWithEmailAndPassword(secondaryAuth, form.email.trim(), form.password);
      await set(ref(db, `workers/${cred.user.uid}`), {
        uid: cred.user.uid,
        staffId,
        department: form.department,
        name: form.name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim(),
        isApproved: true,
        suspended: false,
        payRateGHS: parseFloat(form.payRateGHS) || 0,
        // Default annual leave allotment — editable per worker afterward
        // from the Workers table.
        leaveBalances: { vacation: 15, sick: 10, other: 5 },
        createdAt: new Date().toISOString(),
      });
      await signOut(secondaryAuth);
      await logAdminAction('create_worker_account', form.email.trim(), { name: form.name.trim(), staffId, department: form.department });
      setForm({ name: '', phone: '', email: '', password: '', payRateGHS: '', department: 'CS' });
      alert(`✅ Account created for ${form.email.trim()} — Staff ID: ${staffId}. Share the email, password, and staff ID with them directly.`);
    } catch (e) {
      alert(e.code === 'auth/email-already-in-use' ? 'That email is already in use' : (e.message || 'Error creating account'));
    } finally { setCreating(false); }
  };

  return (
    <div style={{ backgroundColor: '#FFF', borderRadius: 12, border: '1px solid #E5E7EB', padding: 20, marginBottom: 20 }}>
      {source && (
        <div style={{ display: 'inline-flex', alignItems: 'center', fontSize: 11, fontWeight: 700, color: '#1D9E75', backgroundColor: '#E8F8F2', padding: '4px 10px', borderRadius: 20, marginBottom: 12 }}>
          📍 Creating from: {source}
        </div>
      )}
      <h3 style={{ fontWeight: 700, margin: '0 0 4px', fontSize: 15 }}>➕ Create Worker Account</h3>
      <p style={{ fontSize: 12, color: '#888', margin: '0 0 16px' }}>
        Set their login here, then share the email and password with them — there's no self-signup in the Worker app. A staff ID (e.g. CS1001) is generated automatically from their department.
      </p>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 12 }}>
        <input style={inp} placeholder="Full name" value={form.name} onChange={e => upd('name', e.target.value)} />
        <input style={inp} placeholder="Phone" value={form.phone} onChange={e => upd('phone', e.target.value)} />
        <select style={inp} value={form.department} onChange={e => upd('department', e.target.value)}>
          {DEPARTMENTS.map(d => <option key={d.code} value={d.code}>{d.label}</option>)}
        </select>
        <input style={inp} type="email" placeholder="Email" value={form.email} onChange={e => upd('email', e.target.value)} />
        <input style={inp} type="password" placeholder="Temporary password" value={form.password} onChange={e => upd('password', e.target.value)} />
        <input style={inp} type="number" min="0" step="0.5" placeholder="Hourly rate GH₵" value={form.payRateGHS} onChange={e => upd('payRateGHS', e.target.value)} />
      </div>
      <button
        style={{ padding: '10px 18px', backgroundColor: creating ? '#A8D5C2' : '#1D9E75', color: '#FFF', border: 'none', borderRadius: 8, cursor: creating ? 'not-allowed' : 'pointer', fontWeight: 700, fontSize: 13 }}
        onClick={createWorker}
        disabled={creating}
      >
        {creating ? 'Creating...' : '✓ Create Worker Account'}
      </button>
    </div>
  );
}

// ── WORKERS ──────────────────────────────────────────────────────────────
function WorkersView({ workers, search, setSearch, updating, approveWorker, toggleSuspend, rateEdits, setRateEdits, saveRate, onEditBalances }) {
  return (
    <div>
      <CreateWorkerForm source="HR & Payroll" />

      <input
        style={{ ...inp, width: 260, marginBottom: 16 }}
        placeholder="Search workers..."
        value={search}
        onChange={e => setSearch(e.target.value)}
      />

      {workers.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 50, backgroundColor: '#FFF', borderRadius: 12, border: '1px solid #E5E7EB' }}>
          <p style={{ fontSize: 36 }}>👤</p>
          <p style={{ fontWeight: 600, color: '#888' }}>No workers yet</p>
          <p style={{ fontSize: 12, color: '#AAA' }}>Create an account above to add your first worker</p>
        </div>
      ) : (
        <div style={{ backgroundColor: '#FFF', borderRadius: 12, border: '1px solid #E5E7EB', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ backgroundColor: '#F7F8FA' }}>
                {['Staff ID', 'Worker', 'Phone', 'Hourly Rate (GH₵)', 'MoMo', 'Status', 'Action'].map(h => (
                  <th key={h} style={{ padding: '12px 14px', textAlign: 'left', fontWeight: 600, color: '#555', borderBottom: '1px solid #E5E7EB', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {workers.map((w, i) => {
                const badge = StatusBadge(w);
                const editingRate = rateEdits[w.id] !== undefined ? rateEdits[w.id] : (w.payRateGHS || '');
                return (
                  <tr key={w.id} style={{ backgroundColor: w.suspended ? '#FFF5F5' : i % 2 === 0 ? '#FFF' : '#FAFAFA' }}>
                    <td style={{ padding: '12px 14px', borderBottom: '1px solid #F3F4F6' }}>
                      <span style={{ fontSize: 11, backgroundColor: '#E8F8F2', color: '#1D9E75', padding: '2px 8px', borderRadius: 20, fontWeight: 700 }}>{w.staffId || '—'}</span>
                    </td>
                    <td style={{ padding: '12px 14px', borderBottom: '1px solid #F3F4F6' }}>
                      <p style={{ fontWeight: 700, margin: '0 0 2px', color: '#1A1A1A' }}>{w.name || '—'}</p>
                      <p style={{ fontSize: 11, color: '#888', margin: 0 }}>{w.email} · {departmentLabel(w.department)}</p>
                    </td>
                    <td style={{ padding: '12px 14px', borderBottom: '1px solid #F3F4F6', color: '#555' }}>{w.phone || '—'}</td>
                    <td style={{ padding: '12px 14px', borderBottom: '1px solid #F3F4F6' }}>
                      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                        <input
                          type="number" min="0" step="0.5"
                          style={{ ...inp, width: 80, padding: '6px 8px' }}
                          value={editingRate}
                          onChange={e => setRateEdits(prev => ({ ...prev, [w.id]: e.target.value }))}
                        />
                        {rateEdits[w.id] !== undefined && rateEdits[w.id] !== String(w.payRateGHS || '') && (
                          <button
                            style={{ padding: '5px 10px', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 11, fontWeight: 700, backgroundColor: '#1D9E75', color: '#FFF' }}
                            onClick={() => saveRate(w)}
                            disabled={updating === w.id}
                          >
                            Save
                          </button>
                        )}
                      </div>
                    </td>
                    <td style={{ padding: '12px 14px', borderBottom: '1px solid #F3F4F6', color: '#555', fontSize: 12 }}>
                      {w.momoNumber ? `${w.momoNumber} (${w.momoNetwork || 'mtn'})` : <span style={{ color: '#EF4444' }}>Not set</span>}
                    </td>
                    <td style={{ padding: '12px 14px', borderBottom: '1px solid #F3F4F6' }}>
                      <span style={{ backgroundColor: badge.bg, color: badge.color, padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600 }}>{badge.label}</span>
                    </td>
                    <td style={{ padding: '12px 14px', borderBottom: '1px solid #F3F4F6', display: 'flex', gap: 6 }}>
                      {!w.suspended && !w.isApproved && (
                        <button
                          style={{ padding: '6px 14px', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 700, backgroundColor: '#DCFCE7', color: '#15803D', opacity: updating === w.id ? 0.6 : 1 }}
                          onClick={() => { if (window.confirm(`Approve ${w.name}?`)) approveWorker(w); }}
                          disabled={updating === w.id}
                        >
                          {updating === w.id ? '...' : '✅ Approve'}
                        </button>
                      )}
                      <button
                        style={{ padding: '6px 14px', border: '1px solid #E5E7EB', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 700, backgroundColor: '#FFF', color: '#374151' }}
                        onClick={() => onEditBalances(w)}
                      >
                        🌴 Leave
                      </button>
                      <button
                        style={{ padding: '6px 14px', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 700, backgroundColor: w.suspended ? '#DCFCE7' : '#FEE2E2', color: w.suspended ? '#15803D' : '#B91C1C', opacity: updating === w.id ? 0.6 : 1 }}
                        onClick={() => {
                          if (window.confirm(w.suspended ? `Reactivate ${w.name}?` : `Suspend ${w.name}? They will not be able to clock in.`)) toggleSuspend(w);
                        }}
                        disabled={updating === w.id}
                      >
                        {updating === w.id ? '...' : w.suspended ? '✅ Reactivate' : '🚫 Suspend'}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── LEAVE BALANCE EDIT MODAL ──────────────────────────────────────────────
function LeaveBalanceModal({ worker, saving, onSave, onClose }) {
  const current = worker.leaveBalances || { vacation: 15, sick: 10, other: 5 };
  const [vacation, setVacation] = useState(String(current.vacation ?? 15));
  const [sick, setSick] = useState(String(current.sick ?? 10));
  const [other, setOther] = useState(String(current.other ?? 5));

  const save = () => {
    const balances = { vacation: parseFloat(vacation) || 0, sick: parseFloat(sick) || 0, other: parseFloat(other) || 0 };
    onSave(balances);
  };

  return (
    <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={onClose}>
      <div style={{ backgroundColor: '#FFF', borderRadius: 14, padding: 24, width: 380, maxWidth: '90vw' }} onClick={e => e.stopPropagation()}>
        <h3 style={{ fontWeight: 700, margin: '0 0 4px', fontSize: 16 }}>🌴 {worker.name}'s Leave Allotment</h3>
        <p style={{ fontSize: 12, color: '#888', margin: '0 0 18px' }}>Annual days allowed per leave type.</p>
        {[
          ['Vacation', vacation, setVacation],
          ['Sick', sick, setSick],
          ['Other', other, setOther],
        ].map(([label, val, setter]) => (
          <div key={label} style={{ marginBottom: 12 }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#444', marginBottom: 6 }}>{label} (days/year)</label>
            <input style={inp} type="number" min="0" step="1" value={val} onChange={e => setter(e.target.value)} />
          </div>
        ))}
        <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
          <button style={{ flex: 1, padding: '10px', border: '1px solid #E5E7EB', borderRadius: 8, cursor: 'pointer', backgroundColor: '#F7F8FA', color: '#555', fontSize: 13 }} onClick={onClose}>Cancel</button>
          <button style={{ flex: 2, padding: '10px', backgroundColor: saving ? '#A8D5C2' : '#1D9E75', color: '#FFF', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 700, fontSize: 13 }} onClick={save} disabled={saving}>
            {saving ? 'Saving...' : '✓ Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── LEAVE REQUESTS ────────────────────────────────────────────────────────
function LeaveRequestsView({ requests, updating, onReview }) {
  const sorted = [...requests].sort((a, b) => {
    if (a.status === 'pending' && b.status !== 'pending') return -1;
    if (a.status !== 'pending' && b.status === 'pending') return 1;
    return new Date(b.requestedAt) - new Date(a.requestedAt);
  });
  const typeLabel = { vacation: '🌴 Vacation', sick: '🤒 Sick', other: '📋 Other' };

  return (
    <div>
      {sorted.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 50, backgroundColor: '#FFF', borderRadius: 12, border: '1px solid #E5E7EB' }}>
          <p style={{ fontSize: 36 }}>🌴</p>
          <p style={{ fontWeight: 600, color: '#888' }}>No leave requests yet</p>
        </div>
      ) : (
        <div style={{ backgroundColor: '#FFF', borderRadius: 12, border: '1px solid #E5E7EB', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ backgroundColor: '#F7F8FA' }}>
                {['Worker', 'Type', 'Dates', 'Days', 'Reason', 'Status', 'Action'].map(h => (
                  <th key={h} style={{ padding: '12px 14px', textAlign: 'left', fontWeight: 600, color: '#555', borderBottom: '1px solid #E5E7EB', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sorted.map((r, i) => (
                <tr key={r.id} style={{ backgroundColor: r.status === 'pending' ? '#FFFBEB' : i % 2 === 0 ? '#FFF' : '#FAFAFA' }}>
                  <td style={{ padding: '12px 14px', borderBottom: '1px solid #F3F4F6', fontWeight: 600 }}>{r.workerName}</td>
                  <td style={{ padding: '12px 14px', borderBottom: '1px solid #F3F4F6' }}>{typeLabel[r.type] || r.type}</td>
                  <td style={{ padding: '12px 14px', borderBottom: '1px solid #F3F4F6', fontSize: 12, color: '#555' }}>{r.startDate} → {r.endDate}</td>
                  <td style={{ padding: '12px 14px', borderBottom: '1px solid #F3F4F6' }}>{r.days}</td>
                  <td style={{ padding: '12px 14px', borderBottom: '1px solid #F3F4F6', maxWidth: 220, fontSize: 12, color: '#555' }}>{r.reason}</td>
                  <td style={{ padding: '12px 14px', borderBottom: '1px solid #F3F4F6' }}>
                    <span style={{
                      backgroundColor: r.status === 'approved' ? '#DCFCE7' : r.status === 'rejected' ? '#FEE2E2' : '#FEF3C7',
                      color: r.status === 'approved' ? '#15803D' : r.status === 'rejected' ? '#B91C1C' : '#92400E',
                      padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700,
                    }}>
                      {r.status === 'approved' ? '✅ Approved' : r.status === 'rejected' ? '❌ Rejected' : '⏳ Pending'}
                    </span>
                  </td>
                  <td style={{ padding: '12px 14px', borderBottom: '1px solid #F3F4F6', display: 'flex', gap: 6 }}>
                    {r.status === 'pending' && (
                      <>
                        <button
                          style={{ padding: '5px 12px', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 700, backgroundColor: '#DCFCE7', color: '#15803D', opacity: updating === r.id ? 0.6 : 1 }}
                          onClick={() => onReview(r, 'approved')}
                          disabled={updating === r.id}
                        >
                          ✅ Approve
                        </button>
                        <button
                          style={{ padding: '5px 12px', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 700, backgroundColor: '#FEE2E2', color: '#B91C1C', opacity: updating === r.id ? 0.6 : 1 }}
                          onClick={() => onReview(r, 'rejected')}
                          disabled={updating === r.id}
                        >
                          ❌ Reject
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── SEND UPDATE (broadcast to a department or everyone) ─────────────────
// Writes the message onto every matching worker's own record
// (workers/{id}/announcements/{id}) rather than a shared top-level node a
// worker could read — same mirrored-index pattern used for payslips and
// leave requests, since a worker can't read the top-level announcements
// node (admin-only), only their own workers/{id} subtree.
function SendUpdateView({ workers }) {
  const [department, setDepartment] = useState('all');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [sentLog, setSentLog] = useState([]);

  useEffect(() => {
    return onValue(ref(db, 'announcements'), snap => {
      setSentLog(snap.exists() ? Object.values(snap.val()).sort((a, b) => new Date(b.sentAt) - new Date(a.sentAt)) : []);
    });
  }, []);

  const targetCount = department === 'all' ? workers.length : workers.filter(w => w.department === department).length;

  const send = async () => {
    if (!message.trim()) { alert('Write a message first'); return; }
    const targets = department === 'all' ? workers : workers.filter(w => w.department === department);
    if (targets.length === 0) { alert('No workers match that department yet'); return; }
    if (!window.confirm(`Send this update to ${targets.length} worker${targets.length === 1 ? '' : 's'}?`)) return;

    setSending(true);
    try {
      const annRef = push(ref(db, 'announcements'));
      const payload = {
        id: annRef.key,
        message: message.trim(),
        department,
        departmentLabel: department === 'all' ? 'All Workers' : departmentLabel(department),
        sentAt: new Date().toISOString(),
        sentBy: auth.currentUser?.email || '',
        recipientCount: targets.length,
      };
      const updates = { [`announcements/${annRef.key}`]: payload };
      targets.forEach(w => { updates[`workers/${w.id}/announcements/${annRef.key}`] = payload; });
      await update(ref(db), updates);
      await logAdminAction('send_worker_update', payload.departmentLabel, { message: payload.message, recipientCount: targets.length });
      setMessage('');
      alert(`✅ Sent to ${targets.length} worker${targets.length === 1 ? '' : 's'}`);
    } catch (e) {
      alert('Error sending update');
    } finally { setSending(false); }
  };

  return (
    <div style={{ maxWidth: 720 }}>
      <div style={{ backgroundColor: '#FFF', borderRadius: 12, border: '1px solid #E5E7EB', padding: 20, marginBottom: 20 }}>
        <h3 style={{ fontWeight: 700, margin: '0 0 4px', fontSize: 15 }}>📢 Send Update</h3>
        <p style={{ fontSize: 12, color: '#888', margin: '0 0 16px' }}>
          Sends straight to the Worker app's Home screen for everyone in the department you pick.
        </p>

        <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#444', marginBottom: 6 }}>Send to</label>
        <select style={{ ...inp, width: '100%', marginBottom: 14 }} value={department} onChange={e => setDepartment(e.target.value)}>
          <option value="all">All Workers ({workers.length})</option>
          {DEPARTMENTS.map(d => (
            <option key={d.code} value={d.code}>{d.label} ({workers.filter(w => w.department === d.code).length})</option>
          ))}
        </select>

        <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#444', marginBottom: 6 }}>Message</label>
        <textarea
          style={{ ...inp, width: '100%', minHeight: 90, fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box' }}
          placeholder="What do you want to tell them?"
          value={message}
          onChange={e => setMessage(e.target.value)}
        />

        <button
          style={{ marginTop: 14, padding: '10px 18px', backgroundColor: sending ? '#A8D5C2' : '#1D9E75', color: '#FFF', border: 'none', borderRadius: 8, cursor: sending ? 'not-allowed' : 'pointer', fontWeight: 700, fontSize: 13 }}
          onClick={send}
          disabled={sending}
        >
          {sending ? 'Sending...' : `📤 Send to ${targetCount} worker${targetCount === 1 ? '' : 's'}`}
        </button>
      </div>

      {sentLog.length > 0 && (
        <div style={{ backgroundColor: '#FFF', borderRadius: 12, border: '1px solid #E5E7EB', padding: 20 }}>
          <h3 style={{ fontWeight: 700, margin: '0 0 14px', fontSize: 15 }}>Sent History</h3>
          {sentLog.map((a, i) => (
            <div key={i} style={{ padding: '12px 0', borderBottom: i < sentLog.length - 1 ? '0.5px solid #F3F4F6' : 'none' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: '#1D9E75' }}>{a.departmentLabel} · {a.recipientCount} recipient{a.recipientCount === 1 ? '' : 's'}</span>
                <span style={{ fontSize: 11, color: '#AAA' }}>{new Date(a.sentAt).toLocaleString()}</span>
              </div>
              <p style={{ fontSize: 13, color: '#333', margin: 0 }}>{a.message}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── RECRUITMENT (public application link + full application form) ───────
// Admin creates a job + a handful of screening questions here, gets back a
// shareable URL on pharmdev.org (pharmdev.org/apply.html?id=<linkId>) —
// deliberately never this admin app's own domain, so a candidate with no
// PharmDev account is never handed anything that touches admin.pharmdev.org.
// Their full application lands back here via the public backend for review
// before anyone's invited to a real interview.
function RecruitmentView() {
  const [links, setLinks] = useState([]);
  const [applications, setApplications] = useState([]);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ title: '', department: 'CS', description: '', questions: DEFAULT_SCREENING_QUESTIONS });
  const [expandedLink, setExpandedLink] = useState(null);
  const [expandedApp, setExpandedApp] = useState(null);
  const [updating, setUpdating] = useState(null);

  useEffect(() => {
    const unsub1 = onValue(ref(db, 'recruitmentLinks'), snap => {
      setLinks(snap.exists() ? Object.entries(snap.val()).map(([id, v]) => ({ id, ...v })) : []);
    });
    const unsub2 = onValue(ref(db, 'recruitmentApplications'), snap => {
      setApplications(snap.exists() ? Object.entries(snap.val()).map(([id, v]) => ({ id, ...v })) : []);
    });
    return () => { unsub1(); unsub2(); };
  }, []);

  const updateQuestion = (i, val) => setForm(f => { const qs = [...f.questions]; qs[i] = val; return { ...f, questions: qs }; });
  const addQuestion = () => setForm(f => ({ ...f, questions: [...f.questions, ''] }));
  const removeQuestion = (i) => setForm(f => ({ ...f, questions: f.questions.filter((_, idx) => idx !== i) }));

  const createLink = async () => {
    if (!form.title.trim()) return alert('Enter a job title');
    const questions = form.questions.map(q => q.trim()).filter(Boolean);
    if (questions.length === 0) return alert('Add at least one screening question');
    setCreating(true);
    try {
      const linkRef = push(ref(db, 'recruitmentLinks'));
      await set(linkRef, {
        id: linkRef.key,
        title: form.title.trim(),
        department: form.department,
        departmentLabel: departmentLabel(form.department),
        description: form.description.trim(),
        questions,
        active: true,
        createdAt: new Date().toISOString(),
        createdBy: auth.currentUser?.email || '',
      });
      await logAdminAction('create_recruitment_link', form.title.trim(), { department: form.department, questionCount: questions.length });
      setForm({ title: '', department: 'CS', description: '', questions: DEFAULT_SCREENING_QUESTIONS });
    } catch { alert('Error creating link'); }
    finally { setCreating(false); }
  };

  const toggleLinkActive = async (link) => {
    await update(ref(db, `recruitmentLinks/${link.id}`), { active: !link.active });
    await logAdminAction(link.active ? 'close_recruitment_link' : 'reopen_recruitment_link', link.title);
  };

  const reviewApplication = async (app, status) => {
    setUpdating(app.id);
    try {
      await update(ref(db, `recruitmentApplications/${app.id}`), { status, reviewedAt: new Date().toISOString(), reviewedBy: auth.currentUser?.email || '' });
      await logAdminAction('review_recruitment_application', app.name, { status, jobTitle: app.jobTitle });
    } catch { alert('Error updating application'); }
    finally { setUpdating(null); }
  };

  // Always the public marketing site, never this admin app's own origin —
  // a candidate must never be handed a link that touches admin.pharmdev.org.
  const copyLink = (linkId) => {
    const url = `https://pharmdev.org/apply.html?id=${linkId}`;
    if (navigator.clipboard?.writeText) navigator.clipboard.writeText(url);
    alert(`Link copied — share this with the candidate:\n\n${url}`);
  };

  const statusStyle = {
    shortlisted: { bg: '#DCFCE7', color: '#15803D', label: '⭐ Shortlisted' },
    hired:       { bg: '#DBEAFE', color: '#1D4ED8', label: '🎉 Hired' },
    rejected:    { bg: '#FEE2E2', color: '#B91C1C', label: '❌ Rejected' },
    submitted:   { bg: '#FEF3C7', color: '#92400E', label: '📥 Submitted' },
  };

  return (
    <div>
      <div style={{ backgroundColor: '#FFF', borderRadius: 12, border: '1px solid #E5E7EB', padding: 20, marginBottom: 20 }}>
        <h3 style={{ fontWeight: 700, margin: '0 0 4px', fontSize: 15 }}>➕ Create Job Application Link</h3>
        <p style={{ fontSize: 12, color: '#888', margin: '0 0 16px' }}>
          Share the link with a candidate — they answer a short screening questionnaire with no account needed. Review
          their answers here before inviting anyone to a full interview.
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 10, marginBottom: 10 }}>
          <input style={inp} placeholder="Job title (e.g. Customer Service Rep)" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} />
          <select style={inp} value={form.department} onChange={e => setForm(f => ({ ...f, department: e.target.value }))}>
            {DEPARTMENTS.map(d => <option key={d.code} value={d.code}>{d.label}</option>)}
          </select>
        </div>
        <textarea
          style={{ ...inp, width: '100%', minHeight: 60, marginBottom: 14, boxSizing: 'border-box', fontFamily: 'inherit', resize: 'vertical' }}
          placeholder="Short role description (optional) — shown to the candidate"
          value={form.description}
          onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
        />

        <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#444', marginBottom: 8 }}>Screening questions</label>
        {form.questions.map((q, i) => (
          <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
            <input style={{ ...inp, flex: 1 }} placeholder={`Question ${i + 1}`} value={q} onChange={e => updateQuestion(i, e.target.value)} />
            {form.questions.length > 1 && (
              <button style={{ padding: '0 12px', border: '1px solid #E5E7EB', borderRadius: 8, cursor: 'pointer', backgroundColor: '#FFF', color: '#B91C1C' }} onClick={() => removeQuestion(i)}>✕</button>
            )}
          </div>
        ))}
        <button style={{ padding: '6px 12px', border: '1px dashed #CBD5E1', borderRadius: 8, cursor: 'pointer', backgroundColor: 'transparent', color: '#1D9E75', fontSize: 12, fontWeight: 600, marginBottom: 14 }} onClick={addQuestion}>
          + Add question
        </button>

        <button
          style={{ display: 'block', padding: '10px 18px', backgroundColor: creating ? '#A8D5C2' : '#1D9E75', color: '#FFF', border: 'none', borderRadius: 8, cursor: creating ? 'not-allowed' : 'pointer', fontWeight: 700, fontSize: 13 }}
          onClick={createLink}
          disabled={creating}
        >
          {creating ? 'Creating...' : '✓ Create Application Link'}
        </button>
      </div>

      {links.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 50, backgroundColor: '#FFF', borderRadius: 12, border: '1px solid #E5E7EB' }}>
          <p style={{ fontSize: 36 }}>🧑‍💻</p>
          <p style={{ fontWeight: 600, color: '#888' }}>No job links yet</p>
          <p style={{ fontSize: 12, color: '#AAA' }}>Create one above to start receiving applications</p>
        </div>
      ) : (
        [...links].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)).map(link => {
          const linkApps = applications.filter(a => a.linkId === link.id).sort((a, b) => new Date(b.submittedAt) - new Date(a.submittedAt));
          const expanded = expandedLink === link.id;
          return (
            <div key={link.id} style={{ backgroundColor: '#FFF', borderRadius: 12, border: '1px solid #E5E7EB', padding: 18, marginBottom: 14 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
                    <span style={{ fontWeight: 700, fontSize: 15 }}>{link.title}</span>
                    <span style={{ fontSize: 11, backgroundColor: '#E8F8F2', color: '#1D9E75', padding: '2px 8px', borderRadius: 20, fontWeight: 700 }}>{link.departmentLabel}</span>
                    {!link.active && <span style={{ fontSize: 11, backgroundColor: '#FEE2E2', color: '#B91C1C', padding: '2px 8px', borderRadius: 20, fontWeight: 700 }}>Closed</span>}
                  </div>
                  <p style={{ fontSize: 12, color: '#888', margin: 0 }}>
                    {linkApps.length} application{linkApps.length === 1 ? '' : 's'} · {(link.questions || []).length} screening question{(link.questions || []).length === 1 ? '' : 's'}
                  </p>
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <button style={{ padding: '6px 12px', border: '1px solid #E5E7EB', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 600, backgroundColor: '#F7F8FA', color: '#1D9E75' }} onClick={() => copyLink(link.id)}>
                    🔗 Copy Link
                  </button>
                  <button
                    style={{ padding: '6px 12px', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 600, backgroundColor: link.active ? '#FEE2E2' : '#DCFCE7', color: link.active ? '#B91C1C' : '#15803D' }}
                    onClick={() => toggleLinkActive(link)}
                  >
                    {link.active ? '🚫 Close' : '✅ Reopen'}
                  </button>
                  <button style={{ padding: '6px 12px', border: '1px solid #E5E7EB', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 600, backgroundColor: '#FFF', color: '#374151' }} onClick={() => setExpandedLink(expanded ? null : link.id)}>
                    {expanded ? 'Hide' : 'View'} Applications
                  </button>
                </div>
              </div>

              {expanded && (
                <div style={{ marginTop: 16, borderTop: '1px solid #F0F0F0', paddingTop: 14 }}>
                  {linkApps.length === 0 ? (
                    <p style={{ fontSize: 12, color: '#AAA', textAlign: 'center', padding: '10px 0' }}>No applications yet.</p>
                  ) : linkApps.map(app => {
                    const badge = statusStyle[app.status] || statusStyle.submitted;
                    return (
                      <div key={app.id} style={{ border: '1px solid #F0F0F0', borderRadius: 10, padding: 14, marginBottom: 10 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10, flexWrap: 'wrap' }}>
                          <div>
                            <p style={{ fontWeight: 700, margin: '0 0 2px' }}>{app.name}</p>
                            <p style={{ fontSize: 12, color: '#888', margin: 0 }}>{app.email} · {app.phone} · {new Date(app.submittedAt).toLocaleString()}</p>
                          </div>
                          <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 20, backgroundColor: badge.bg, color: badge.color }}>{badge.label}</span>
                        </div>
                        <div style={{ marginTop: 10 }}>
                          {(app.questions || []).map((q, i) => (
                            <div key={i} style={{ marginBottom: 8 }}>
                              <p style={{ fontSize: 12, fontWeight: 600, color: '#555', margin: '0 0 2px' }}>{q}</p>
                              <p style={{ fontSize: 13, color: '#333', margin: 0, whiteSpace: 'pre-wrap' }}>{app.answers?.[i] || '—'}</p>
                            </div>
                          ))}
                        </div>
                        <button
                          style={{ padding: '5px 12px', border: '1px solid #E5E7EB', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 600, backgroundColor: '#F7F8FA', color: '#374151', marginTop: 4 }}
                          onClick={() => setExpandedApp(expandedApp === app.id ? null : app.id)}
                        >
                          {expandedApp === app.id ? 'Hide' : 'View'} Full Application
                        </button>
                        {expandedApp === app.id && <ApplicationDetail app={app} />}
                        <div style={{ display: 'flex', gap: 6, marginTop: 10 }}>
                          <button style={{ padding: '5px 12px', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 700, backgroundColor: '#DCFCE7', color: '#15803D', opacity: updating === app.id ? 0.6 : 1 }} onClick={() => reviewApplication(app, 'shortlisted')} disabled={updating === app.id}>
                            ⭐ Shortlist
                          </button>
                          <button style={{ padding: '5px 12px', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 700, backgroundColor: '#DBEAFE', color: '#1D4ED8', opacity: updating === app.id ? 0.6 : 1 }} onClick={() => reviewApplication(app, 'hired')} disabled={updating === app.id}>
                            🎉 Mark Hired
                          </button>
                          <button style={{ padding: '5px 12px', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 700, backgroundColor: '#FEE2E2', color: '#B91C1C', opacity: updating === app.id ? 0.6 : 1 }} onClick={() => reviewApplication(app, 'rejected')} disabled={updating === app.id}>
                            ❌ Reject
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })
      )}
    </div>
  );
}

// Full read-out of the 10-section application (name/phone/email/questions
// were already shown above this in the collapsed row — everything else
// lives here behind "View Full Application" since most applications are
// skimmed, not read end-to-end, at the shortlist stage.
const detailWrap  = { marginTop: 10, borderTop: '1px dashed #E5E7EB', paddingTop: 10 };
const detailHd    = { fontSize: 11, fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: 0.5, margin: '12px 0 6px' };
const detailField = { fontSize: 13, color: '#333', margin: '0 0 4px' };
const detailLabel = { color: '#888', fontWeight: 600 };
const detailChip  = { fontSize: 12, color: '#333', backgroundColor: '#F7F8FA', border: '1px solid #E5E7EB', borderRadius: 8, padding: '8px 10px', marginBottom: 6 };

function ApplicationDetail({ app }) {
  return (
    <div style={detailWrap}>
      <p style={detailHd}>Applicant Information</p>
      <p style={detailField}><span style={detailLabel}>Address:</span> {app.address || '—'}{app.city ? `, ${app.city}` : ''}</p>
      <p style={detailField}><span style={detailLabel}>Emergency contact:</span> {app.emergencyContact?.name || '—'} ({app.emergencyContact?.relationship || 'n/a'}) · {app.emergencyContact?.phone || '—'}</p>

      <p style={detailHd}>Position &amp; Availability</p>
      <p style={detailField}><span style={detailLabel}>Employment type:</span> {app.employmentType === 'part-time' ? 'Part-time' : 'Full-time'}</p>
      {app.desiredSalary && <p style={detailField}><span style={detailLabel}>Desired salary:</span> {app.desiredSalary}</p>}
      <p style={detailField}><span style={detailLabel}>Start date:</span> {app.startDate || '—'}</p>
      <p style={detailField}><span style={detailLabel}>Overtime available:</span> {app.canWorkOvertime ? 'Yes' : 'No'}</p>
      {app.shiftAvailability && <p style={detailField}><span style={detailLabel}>Shift availability:</span> {app.shiftAvailability}</p>}

      <p style={detailHd}>Work Eligibility</p>
      <p style={detailField}><span style={detailLabel}>18 or older:</span> {app.over18 ? 'Yes' : 'No'} · <span style={detailLabel}>Eligible to work in Ghana:</span> {app.eligibleToWorkGhana ? 'Yes' : 'No'} · <span style={detailLabel}>Driver's license:</span> {app.hasDriverLicense ? 'Yes' : 'No'}</p>
      {app.needsAccommodation && <p style={detailField}><span style={detailLabel}>Needs accommodation:</span> {app.accommodationDetails || 'Yes (no details given)'}</p>}
      {app.transportation && <p style={detailField}><span style={detailLabel}>Transportation:</span> {app.transportation}</p>}

      <p style={detailHd}>Education</p>
      {(app.education || []).length === 0 ? <p style={detailField}>—</p> : app.education.map((e, i) => (
        <div key={i} style={detailChip}>
          <strong>{e.school}</strong>{e.degree ? ` · ${e.degree}` : ''}{e.fieldOfStudy ? ` · ${e.fieldOfStudy}` : ''}{e.graduationYear ? ` · ${e.graduationYear}` : ''}
        </div>
      ))}

      <p style={detailHd}>Employment History</p>
      <p style={detailField}><span style={detailLabel}>Currently employed:</span> {app.currentlyEmployed ? 'Yes' : 'No'} · <span style={detailLabel}>Military/national service:</span> {app.hasMilitaryService ? (app.militaryServiceDetails || 'Yes') : 'No'}</p>
      {(app.employmentHistory || []).length === 0 ? <p style={detailField}>No prior employers listed.</p> : app.employmentHistory.map((h, i) => (
        <div key={i} style={detailChip}>
          <strong>{h.employer}</strong>{h.position ? ` — ${h.position}` : ''}<br />
          {h.startDate || '?'} to {h.endDate || '?'}{h.reasonForLeaving ? ` · Left because: ${h.reasonForLeaving}` : ''}
        </div>
      ))}

      <p style={detailHd}>Skills</p>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {(app.skills || []).length === 0 ? <p style={detailField}>—</p> : app.skills.map((s, i) => (
          <span key={i} style={{ ...detailChip, marginBottom: 0 }}>{s.skill} <span style={{ color: '#888' }}>({s.proficiency})</span></span>
        ))}
      </div>

      <p style={detailHd}>References</p>
      {(app.references || []).length === 0 ? <p style={detailField}>—</p> : app.references.map((r, i) => (
        <div key={i} style={detailChip}>
          <strong>{r.name}</strong>{r.relationship ? ` (${r.relationship})` : ''} · {r.phone}{r.email ? ` · ${r.email}` : ''}
        </div>
      ))}

      <p style={detailHd}>Certification</p>
      <p style={detailField}>
        {app.certifyingStatementAgreed ? '✅ Certified accurate' : '⚠️ Not certified'} — signed "<em>{app.signature}</em>" on {app.signedAt ? new Date(app.signedAt).toLocaleDateString() : '—'}
      </p>
    </div>
  );
}

// ── CLOCK-IN STATION ─────────────────────────────────────────────────────
function ClockStationView() {
  const [qrValue, setQrValue] = useState('');
  const [error, setError] = useState('');
  const [secondsLeft, setSecondsLeft] = useState(0);
  const expiresAtRef = useRef(0);

  const fetchToken = async () => {
    try {
      const data = await authedFetch('/worker/clock/qr-token', null, 'GET');
      setQrValue(data.token);
      expiresAtRef.current = Date.now() + data.expiresInMs;
      setError('');
    } catch (e) {
      setError(e.message || 'Could not load QR code');
    }
  };

  useEffect(() => {
    fetchToken();
    const refresh = setInterval(fetchToken, 70 * 1000);
    const tick = setInterval(() => {
      setSecondsLeft(Math.max(0, Math.round((expiresAtRef.current - Date.now()) / 1000)));
    }, 1000);
    return () => { clearInterval(refresh); clearInterval(tick); };
  }, []);

  return (
    <div style={{ backgroundColor: '#FFF', borderRadius: 16, border: '1px solid #E5E7EB', padding: 40, textAlign: 'center', maxWidth: 420, margin: '0 auto' }}>
      <p style={{ fontWeight: 700, fontSize: 16, marginBottom: 6 }}>📷 Clock-In Station</p>
      <p style={{ fontSize: 12, color: '#888', marginBottom: 24 }}>
        Workers scan this from the PharmDev Worker app to clock in or out. It refreshes automatically so a photo of it can't be reused later, and their app also checks they're within 100m of the office before it accepts a scan.
      </p>
      {error ? (
        <p style={{ color: '#EF4444', fontSize: 13 }}>{error}</p>
      ) : qrValue ? (
        <>
          <div style={{ display: 'inline-block', padding: 16, backgroundColor: '#FFF', border: '1px solid #E5E7EB', borderRadius: 12 }}>
            <QRCodeSVG value={qrValue} size={240} />
          </div>
          <p style={{ fontSize: 12, color: secondsLeft <= 15 ? '#EF4444' : '#888', marginTop: 16, fontWeight: 600 }}>
            Refreshes in {secondsLeft}s
          </p>
        </>
      ) : (
        <p style={{ color: '#888', fontSize: 13 }}>Loading…</p>
      )}
    </div>
  );
}

// ── ATTENDANCE LOG ───────────────────────────────────────────────────────
function AttendanceView({ workers, attendance }) {
  const [workerId, setWorkerId] = useState('all');

  const rows = [];
  Object.entries(attendance).forEach(([wId, events]) => {
    if (workerId !== 'all' && wId !== workerId) return;
    const worker = workers.find(w => w.id === wId);
    Object.values(events).forEach(e => {
      rows.push({ workerName: worker?.name || wId, ...e });
    });
  });
  rows.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

  return (
    <div>
      <select style={{ ...inp, width: 240, marginBottom: 16 }} value={workerId} onChange={e => setWorkerId(e.target.value)}>
        <option value="all">All workers</option>
        {workers.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
      </select>

      {rows.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 50, backgroundColor: '#FFF', borderRadius: 12, border: '1px solid #E5E7EB' }}>
          <p style={{ fontSize: 36 }}>🕒</p>
          <p style={{ fontWeight: 600, color: '#888' }}>No clock events yet</p>
        </div>
      ) : (
        <div style={{ backgroundColor: '#FFF', borderRadius: 12, border: '1px solid #E5E7EB', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ backgroundColor: '#F7F8FA' }}>
                {['Worker', 'Event', 'Time', 'Payroll'].map(h => (
                  <th key={h} style={{ padding: '12px 14px', textAlign: 'left', fontWeight: 600, color: '#555', borderBottom: '1px solid #E5E7EB' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.slice(0, 200).map((r, i) => (
                <tr key={i} style={{ backgroundColor: i % 2 === 0 ? '#FFF' : '#FAFAFA' }}>
                  <td style={{ padding: '10px 14px', borderBottom: '1px solid #F3F4F6', fontWeight: 600 }}>{r.workerName}</td>
                  <td style={{ padding: '10px 14px', borderBottom: '1px solid #F3F4F6' }}>
                    {{ in: '🟢 Clocked In', out: '🔴 Clocked Out', break_start: '☕ Break started', break_end: '☕ Break ended' }[r.type] || r.type}
                  </td>
                  <td style={{ padding: '10px 14px', borderBottom: '1px solid #F3F4F6', color: '#888' }}>{new Date(r.timestamp).toLocaleString()}</td>
                  <td style={{ padding: '10px 14px', borderBottom: '1px solid #F3F4F6', fontSize: 11, color: r.payrollProcessed ? '#15803D' : '#AAA' }}>
                    {r.payrollProcessed ? '✅ Paid' : '— Unpaid'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── RUN PAYROLL ───────────────────────────────────────────────────────────
function PayrollView({ workers }) {
  const [periodStart, setPeriodStart] = useState('');
  const [periodEnd, setPeriodEnd] = useState('');
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const runPreview = async () => {
    if (!periodStart || !periodEnd) return alert('Choose a start and end date');
    setLoading(true);
    setResult(null);
    try {
      const data = await authedFetch('/worker/payroll/run', { periodStart, periodEnd, dryRun: true });
      setPreview(data);
    } catch (e) {
      alert(e.message || 'Could not build preview');
    } finally { setLoading(false); }
  };

  const confirmRun = async () => {
    if (!window.confirm(`Pay ${preview.entries.filter(e => e.status !== 'PREVIEW_NO_MOMO').length} worker(s) a total of GH₵ ${preview.entries.reduce((s, e) => s + e.grossPay, 0).toFixed(2)} via Paystack? This cannot be undone.`)) return;
    setLoading(true);
    try {
      const data = await authedFetch('/worker/payroll/run', { periodStart, periodEnd, dryRun: false });
      await logAdminAction('run_worker_payroll', 'batch', { runId: data.runId, periodStart, periodEnd, paid: data.paid, failed: data.failed });
      setResult(data);
      setPreview(null);
    } catch (e) {
      alert(e.message || 'Payroll run failed');
    } finally { setLoading(false); }
  };

  return (
    <div style={{ maxWidth: 720 }}>
      <div style={{ backgroundColor: '#FFF', borderRadius: 12, border: '1px solid #E5E7EB', padding: 20, marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#555', marginBottom: 6 }}>Period start</label>
            <input type="date" style={inp} value={periodStart} onChange={e => setPeriodStart(e.target.value)} />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#555', marginBottom: 6 }}>Period end</label>
            <input type="date" style={inp} value={periodEnd} onChange={e => setPeriodEnd(e.target.value)} />
          </div>
          <button style={{ padding: '10px 18px', backgroundColor: '#1D9E75', color: '#FFF', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 700, fontSize: 13 }} onClick={runPreview} disabled={loading}>
            {loading ? '...' : '👁 Preview'}
          </button>
        </div>
        <p style={{ fontSize: 11, color: '#AAA', marginTop: 10, marginBottom: 0 }}>
          Gross pay only — hours × hourly rate, no SSNIT/PAYE deductions applied in this version.
        </p>
      </div>

      {preview && (
        <div style={{ backgroundColor: '#FFF', borderRadius: 12, border: '1px solid #E5E7EB', overflow: 'hidden', marginBottom: 16 }}>
          {preview.entries.length === 0 ? (
            <p style={{ padding: 30, textAlign: 'center', color: '#888' }}>No unpaid hours found in this period.</p>
          ) : (
            <>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ backgroundColor: '#F7F8FA' }}>
                    {['Worker', 'Hours', 'Rate', 'Gross Pay', 'MoMo'].map(h => (
                      <th key={h} style={{ padding: '12px 14px', textAlign: 'left', fontWeight: 600, color: '#555', borderBottom: '1px solid #E5E7EB' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {preview.entries.map((e, i) => (
                    <tr key={i}>
                      <td style={{ padding: '10px 14px', borderBottom: '1px solid #F3F4F6', fontWeight: 600 }}>{e.name}</td>
                      <td style={{ padding: '10px 14px', borderBottom: '1px solid #F3F4F6' }}>{e.hours}</td>
                      <td style={{ padding: '10px 14px', borderBottom: '1px solid #F3F4F6' }}>GH₵ {e.payRateGHS.toFixed(2)}</td>
                      <td style={{ padding: '10px 14px', borderBottom: '1px solid #F3F4F6', fontWeight: 700, color: '#1D9E75' }}>GH₵ {e.grossPay.toFixed(2)}</td>
                      <td style={{ padding: '10px 14px', borderBottom: '1px solid #F3F4F6' }}>
                        {e.status === 'PREVIEW_NO_MOMO' ? <span style={{ color: '#EF4444', fontSize: 12 }}>⚠️ No MoMo set</span> : '✅'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div style={{ padding: 16, display: 'flex', justifyContent: 'flex-end' }}>
                <button style={{ padding: '11px 22px', backgroundColor: '#1D9E75', color: '#FFF', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 700, fontSize: 13 }} onClick={confirmRun} disabled={loading}>
                  {loading ? 'Paying…' : '💵 Confirm & Pay via Paystack'}
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {result && (
        <div style={{ backgroundColor: '#E8F8F2', borderRadius: 12, border: '1px solid #B2DFD0', padding: 20 }}>
          <p style={{ fontWeight: 700, color: '#1D6B52', margin: '0 0 8px' }}>✅ Payroll run complete</p>
          <p style={{ fontSize: 13, color: '#1D6B52', margin: '0 0 12px' }}>{result.paid} paid, {result.failed} failed</p>
          {result.entries.filter(e => e.status !== 'FAILED').map((e, i) => (
            <a
              key={i}
              href="#"
              onClick={async (ev) => {
                ev.preventDefault();
                try {
                  const token = await auth.currentUser.getIdToken();
                  const res = await fetch(`${BACKEND_URL}/worker/payslip/${result.runId}?workerId=${e.workerId}`, {
                    headers: { 'Authorization': `Bearer ${token}` },
                  });
                  if (!res.ok) throw new Error('Could not load payslip');
                  const blob = await res.blob();
                  window.open(URL.createObjectURL(blob), '_blank');
                } catch (err) {
                  alert(err.message || 'Could not open payslip');
                }
              }}
              style={{ display: 'block', fontSize: 13, color: '#1D9E75', marginBottom: 6 }}
            >
              📄 Download {e.name}'s payslip
            </a>
          ))}
        </div>
      )}
    </div>
  );
}
